import {
  ChatBubbleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross1Icon,
} from '@radix-ui/react-icons';
import { animated, useSpring } from '@react-spring/web';
import { Tooltip } from 'radix-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';

import { useTranslation } from '~/app';
import {
  selectChatDisplayItems,
  selectChatLastReadIncomingEventIndex,
  selectChatUnreadIncomingCount,
  selectPlayers,
  useSelector,
} from '~/app/selectors';
import {
  sendMessage,
  sendSpectatorMessage,
  setChatLastReadIncomingEventIndex,
  setChatPanelState,
} from '~/overlay/actions';
import {
  BAR_BOTTOM_HEIGHT_PERCENT,
  BAR_TOP_HEIGHT_PERCENT,
  FAB_BOTTOM_INSET_REM,
  FAB_EDGE_INSET_REM,
  PANEL_BOTTOM_INSET_REM,
  PANEL_TOP_INSET_REM,
  RESULTS_LAYOUT_FAB_VERTICAL_CLASS,
  RESULTS_LAYOUT_PANEL_VERTICAL,
} from '~/overlay/overlaySpacing';
import { cn } from '~/utils/styles';

import ChatComposer from './ChatComposer';
import ChatMessageList from './ChatMessageList';

export type ChatLayout = 'board' | 'results';

interface ChatProps {
  initVisible?: boolean;
  /** When true, messages are sent as spectator messages (marked in UI and data). */
  isSpectatorMode?: boolean;
  /** When true, sending is disabled (viewing in-game history). */
  isHistoryMode?: boolean;
  /** When false and isSpectatorMode, spectator can only read chat (no send input). Default true. */
  spectatorsCanSend?: boolean;
  /** `results`: viewport-fixed panel with full-height top inset (no zoom-row gap). */
  layout?: ChatLayout;
}

const Chat = ({
  initVisible = true,
  isSpectatorMode = false,
  isHistoryMode = false,
  spectatorsCanSend = true,
  layout = 'board',
}: ChatProps) => {
  const { t } = useTranslation();
  const chatPanelRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const displayItems = useSelector(selectChatDisplayItems);
  const players = useSelector(selectPlayers);
  const chatLastReadIncomingEventIndex = useSelector(selectChatLastReadIncomingEventIndex);
  const unreadIncomingCount = useSelector(selectChatUnreadIncomingCount);

  const canSend = !isHistoryMode && (!isSpectatorMode || spectatorsCanSend);
  const onSendMessage = useCallback(
    (message: string) => {
      if (!canSend) return;
      if (isSpectatorMode) {
        dispatch(sendSpectatorMessage({ message }));
      } else {
        dispatch(sendMessage({ message }));
      }
    },
    [dispatch, isSpectatorMode, canSend],
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(initVisible);

  const displayVisible = isVisible && !isHistoryMode;

  useEffect(() => {
    dispatch(setChatPanelState({ isVisible: displayVisible, isExpanded }));
  }, [displayVisible, isExpanded, dispatch]);

  const requestMarkReadToIncomingEventIndex = useCallback(
    (eventLogIndex: number) => {
      dispatch(setChatLastReadIncomingEventIndex(eventLogIndex));
    },
    [dispatch],
  );

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const chatPanelSpring = useSpring({
    to: {
      width: isExpanded
        ? windowWidth >= 1024
          ? '35vw'
          : '40vw'
        : windowWidth >= 1024
          ? '15vw'
          : '20vw',
      opacity: displayVisible ? 1 : 0,
      transform: displayVisible ? 'translateX(0%)' : 'translateX(100%)',
    },
    config: { tension: 280, friction: 26 },
  });

  const isResultsLayout = layout === 'results';
  const chatPanelFrameStyle = isResultsLayout
    ? RESULTS_LAYOUT_PANEL_VERTICAL
    : {
        top: `calc(${BAR_TOP_HEIGHT_PERCENT}% + ${PANEL_TOP_INSET_REM}rem)`,
        bottom: `calc(${BAR_BOTTOM_HEIGHT_PERCENT}% + ${PANEL_BOTTOM_INSET_REM}rem)`,
      };
  const chatPanelPositionClass = isResultsLayout ? 'fixed' : 'absolute';
  const chatFabBoardStyle: React.CSSProperties = {
    bottom: `calc(${BAR_BOTTOM_HEIGHT_PERCENT}% + ${FAB_BOTTOM_INSET_REM}rem)`,
    right: `${FAB_EDGE_INSET_REM}rem`,
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const toggleVisibility = () => setIsVisible(!isVisible);

  if (!displayVisible) {
    const tooltipOffset = 6;
    const fabButton = (
      <button
        onClick={toggleVisibility}
        style={isResultsLayout ? undefined : chatFabBoardStyle}
        className={cn(
          'pointer-events-auto fixed z-120 flex h-9 w-9 items-center justify-center overflow-visible rounded-full bg-[#405820] text-white shadow-lg md:h-10 md:w-10',
          isResultsLayout && RESULTS_LAYOUT_FAB_VERTICAL_CLASS,
          isResultsLayout && 'right-3',
        )}
        aria-label={t('chat_open_tooltip')}
      >
        <span className="relative inline-flex">
          <ChatBubbleIcon className="h-4 w-4 md:h-5 md:w-5" />
          {unreadIncomingCount > 0 ? (
            <div
              className="absolute -top-[16px] -left-[16px] flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[0.65rem] font-bold text-white shadow"
              aria-hidden
            >
              {unreadIncomingCount > 99 ? '99+' : unreadIncomingCount}
            </div>
          ) : null}
        </span>
      </button>
    );

    const content = (
      <Tooltip.Provider delayDuration={0}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>{fabButton}</Tooltip.Trigger>
          <Tooltip.Content
            side="left"
            sideOffset={tooltipOffset}
            className="rounded-lg border bg-white px-2 py-1 text-sm shadow-lg"
          >
            {t('chat_open_tooltip')}
          </Tooltip.Content>
        </Tooltip.Root>
      </Tooltip.Provider>
    );

    // Render FAB in a body-level portal so `position: fixed` is relative to the viewport
    // even if overlay ancestors apply transforms (which would otherwise break fixed positioning).
    return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
  }

  return (
    <>
      <animated.div
        style={{
          ...chatPanelSpring,
          ...chatPanelFrameStyle,
        }}
        className={cn(
          '@container pointer-events-auto right-0 flex flex-col rounded-tl-lg rounded-bl-lg border-2 border-[#405820]/40 bg-white/90 p-2 select-text',
          chatPanelPositionClass,
        )}
      >
        <div ref={chatPanelRef} className="flex h-full min-h-0 flex-col">
          <div className="absolute top-1/2 left-0 z-10 translate-x-[-50%] -translate-y-1/2 transform">
            <button
              onClick={toggleExpand}
              className="mb-2 block rounded-l-md bg-[#405820] p-2 text-white"
            >
              {isExpanded ? (
                <ChevronRightIcon className="h-4 w-4" />
              ) : (
                <ChevronLeftIcon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={toggleVisibility}
              className="block rounded-l-md bg-[#405820] p-2 text-white"
            >
              <Cross1Icon className="h-4 w-4" />
            </button>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {/* List keeps left inset for chevron / bubble alignment; composer is full width below. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col pl-3">
              <ChatMessageList
                items={displayItems}
                players={players ?? undefined}
                unknownPlayerLabel={t('history_unknown_player')}
                chatLastReadIncomingEventIndex={chatLastReadIncomingEventIndex}
                unreadIncomingCount={unreadIncomingCount}
                onRequestMarkReadToIncomingEventIndex={requestMarkReadToIncomingEventIndex}
              />
            </div>

            {canSend ? (
              <ChatComposer
                canSend={canSend}
                onSend={onSendMessage}
                placeholder={t('chat_placeholder')}
                sendLabel={t('chat_send')}
              />
            ) : (
              <div className="border-t border-slate-200/80 px-1 py-2 text-sm text-slate-500 @[18rem]:px-2">
                {t('chat_read_only_hint')}
              </div>
            )}
          </div>
        </div>
      </animated.div>
    </>
  );
};

export default Chat;
